import type { FunctionComponent, HTMLProps } from 'react'
import React from 'react'

import { slugify } from '../../../utilities/parsers/slugify'
import type { LinkProps } from '../../Link'
import { NavigationLink } from '../../Links/NavigationLink'
import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import styles from './NavigationLinkBlock.module.scss'

export const NavigationLinkBlock: FunctionComponent<Props> = ({
  verticalName,
  className,
  links,
  dataIdentifier,
  verticalHeader = 'All categories in',
  ...rest
}) => (
  <div data-testid="navigation-link-block" {...{ className, ...rest }}>
    {verticalName && (
      <div
        className={styles.navigationLinkBlockVerticalHeader}
        aria-label={`Links to Categories in ${verticalName}`}
      >
        <TypographyV2
          tag="span"
          textStyle="sb-text-interface-body-small-strong"
          className={styles.navigationLinkBlockVerticalHeaderFirst}
        >
          {verticalHeader}
        </TypographyV2>
        <TypographyV2
          tag="h2"
          textStyle="sb-text-heading-large"
          data-testid="vertical-name"
        >
          {verticalName}
        </TypographyV2>
      </div>
    )}
    <nav aria-label={`${verticalName} Links`}>
      <ul className={styles.navigationLinkBlockList}>
        {links.map(({ href, children }, index) => (
          <li
            className={styles.navigationLinkBlockListItem}
            key={`link-${href}-${index}`}
          >
            <NavigationLink
              href={href}
              linkStyle="secondary"
              textStyle="sb-text-heading-small"
              className={styles.navigationLinkBlockLink}
              data-testid={`navigation-link-${index}-${slugify(
                String(children)
              )}`}
              data-which-id={dataIdentifier && `${dataIdentifier}-${index}`}
            >
              {children}
            </NavigationLink>
          </li>
        ))}
      </ul>
    </nav>
  </div>
)

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Array of link objects to be rendered as individual link components
   */
  links: Array<LinkProps>
  /**
   * Name of vertical section the links belong to, to be displayed in the component header
   */
  verticalName?: string
  /**
   * Class name to be added to component for extra styling purposes
   */
  className?: string
  /**
   * String that will be used within the data-which-id element on each link generated by the component
   */
  dataIdentifier?: string
  /**
   * String that will be used as the vertical header
   */
  verticalHeader?: string
} & HTMLProps<HTMLDivElement>
